/**
 * Helpers para descargas autenticadas desde el admin panel.
 *
 * Patrón: fetch con cookies → parsear headers de feedback → trigger
 * download via blob anchor → toast de éxito/error. Usado por exports
 * de declaraciones (Hechauka), reportes, etc.
 */

import { toast } from '@/components/ui/sonner';

interface HechaukaStats {
  included: number;
  skipped: number;
  montoTotal?: number;
  ivaCredito10?: number;
  ivaCredito5?: number;
}

/**
 * Descarga el TXT Hechauka del Formulario 211 (Libro de Compras) para una
 * declaración IVA. El servidor responde con encoding latin1 (Windows-1252)
 * y un header `X-Hechauka-Stats` con resumen — lo mostramos en el toast.
 *
 * Errors:
 *  - HTTP 404: declaración no existe → toast con detail.
 *  - HTTP 409: declaración sin facturas o tipo no soportado → toast.
 *  - HTTP 5xx u otros: toast genérico con el HTTP code.
 */
export async function downloadHechauka(declarationId: string): Promise<void> {
  try {
    const resp = await fetch(
      `/api/admin/declarations/${declarationId}/export?format=hechauka`,
      { credentials: 'include' }
    );
    if (!resp.ok) {
      let detail = `HTTP ${resp.status}`;
      try {
        const body = await resp.json();
        detail = body.error ?? detail;
      } catch {
        /* body no era JSON, dejamos el HTTP code */
      }
      toast.error(`No se pudo generar Hechauka: ${detail}`);
      return;
    }

    // Stats opcionales del header — si vienen, las mostramos en el toast.
    let statsMsg = '';
    const statsHeader = resp.headers.get('X-Hechauka-Stats');
    if (statsHeader) {
      try {
        const stats = JSON.parse(statsHeader) as HechaukaStats;
        statsMsg = ` (${stats.included} facturas${
          stats.skipped > 0 ? `, ${stats.skipped} omitidas` : ''
        })`;
      } catch {
        /* header malformado, ignorar */
      }
    }

    // Nombre desde Content-Disposition; fallback a hechauka_<id>.txt.
    const disposition = resp.headers.get('Content-Disposition') ?? '';
    const filenameMatch = /filename="?([^";]+)"?/i.exec(disposition);
    const filename = filenameMatch?.[1] ?? `hechauka_${declarationId}.txt`;

    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);

    toast.success(`Hechauka descargado${statsMsg}`);
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    toast.error(`Error descargando Hechauka: ${msg}`);
  }
}
